// kernel/useLeasingWorkflows.ts
import { markEnriching } from '#lib/leasing/enrichingDims';
import { ENRICH_DIMS } from '#lib/leasing/enrichNote';
import { useCallback, useRef } from 'react';
import { sourceActionFailure } from '#lib/source-actions';
import { resolveProjectStudioSection, restoreProjectToolView } from '#lib/studio-project-view';
import { rentRollPayload } from '#lib/leasing/sourceRentRoll';
import { canEditSourceRentRoll } from '#lib/access/sourceAccess';
import {
  type KernelHandle,
  type useLeasingWorkflowsReturnType,
  type LeasingLeadDTO,
  type AuditResolutionAction,
  type LeasingActionType,
  type EntityType,
  type GeneratedDocumentType,
  type SessionTableFocusView,
  type SourceBackNavigationOutcome,
  type ActiveEntityFocus,
  type BrandDataCheck,
  type BrandExpansionSignal,
  type BrandNews,
  type BrandGoogleSignals,
  type SourcingSelectionTriple,
  type SourcingTargetRuntimeState,
  type SourcingTargetSelection,
  type ContactDiscoveryEnvelope,
  type ContactDiscoveryEnvelopeV2,
  type ContactDiscoveryEnvelopeLegacy,
  type ContactDiscoverySummary,
  type ContactDiscoveryResult,
} from '#types/types';
import type { PortalUrlState } from '#lib/leasing/portal-url-state';
import { normalizeSourcingSelection } from '#lib/leasing/sourcingSelection';
import { toast } from 'sonner';
import axios from 'axios';
import {
  deriveCreationScopeFromSubject,
  getSessionTableFocusViewForEntityType,
  isEntityFocusSubject,
  isTableFocusSubject,
  resolvePortalViewFromTableFocus,
  resolveTableFocusFromPortalView,
  selectActiveSubjectFromInventoryState,
  SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE,
} from '#lib/leasing/session-focus';
import {
  resolveDefaultSourcePortfolioId,
  resolveSubjectOwningPortfolioId,
  SOURCE_DOMAIN_ENTITY_TYPES,
  SOURCE_EMPTY_PORTFOLIO_ID,
} from '#lib/source-defaults';
import {
  resolveDealEconomicsTabForAction,
  resolveDealWorkspaceTabForAction,
  sanitizeDealEconomicsTab,
} from '@/components/entities/pages/DealPage';

// Enrich a few brands at a time, bulk-enriching every selected brand at once would
// hammer the providers (and the user's credits); a small pool keeps it civil.
const BULK_ENRICH_CONCURRENCY = 3;

interface useLeasingWorkflowsArgs {
  kernel: React.RefObject<KernelHandle | null>;
}

type ScopedHomeSessionRow = { id: string; updatedAtMs: number; messageCount: number };

// Entering Source asks "which conversation is this entity's home?" from five different
// surfaces at once - the nav click, the sourcing center, the canvas top bar, the right
// sidebar and the URL synchronizer - and React mounts them in the same tick. Each asked the
// server on its own, so one click became ~30 identical requests; with a six-connection pool
// and four queries fired per request they convoyed, and the last answers landed minutes
// later while the page sat empty. Measured on the preview 2026-08-12: 30 identical calls,
// the first answering in 3.8s and the slowest in 5.1min, though every query behind them runs
// in under 20ms.
// It is one question about one entity, so it is asked once and the answer is shared. Keyed by
// the scope that defines the answer; the entry is dropped as soon as it settles, so this is a
// concurrency guard and never a cache - the next open asks again.
const inFlightScopedHomeSessions = new Map<string, Promise<ScopedHomeSessionRow[]>>();

// Hardcoded toggle matrix: controls whether entity focus should auto-resume
// the latest message-bearing session when current session is navigation-only.
const ENTITY_FOCUS_SESSION_RELOAD_POLICY = {
  Property: false,
  Floor: false,
  Unit: false,
  UnitPosition: false,
  Portfolio: false,
  Brand: false,
  BrandLocation: false,
  LegalEntityFinancialRecord: false,
  LegalEntity: false,
  MarketSignal: false,
  Campaign: false,
  SourcingCampaign: false,
  PortfolioFitCampaign: false,
  PortfolioFit: false,
  Deal: false,
  ChatSession: false,
  GeneratedDocument: false,
  Lead: false,
  Project: true,
  Organisation: false,
  User: false,
  Notification: false,
  Contact: false,
  Audit: false,
  SlideCorrection: false,
  UniversalFact: false,
  StudioWorkspace: false,
  StudioLayer: false,
  StudioWorkspaceVersion: false,
} satisfies Record<EntityType, boolean>;

/**
 * Contact discovery answers with either the current V2 envelope (counters under `counts`, budget
 * under `cost`) or the historical flat one. Both collapse to the same five counters here, so no
 * caller has to know which schema it got.
 */
const summarizeContactDiscovery = (
  discovery: ContactDiscoveryEnvelope | null | undefined
): ContactDiscoverySummary => {
  const isV2 = discovery?.schemaVersion === 'CONTACT_DISCOVERY_V2';
  const v2 = isV2 ? (discovery as ContactDiscoveryEnvelopeV2) : null;
  const legacy = isV2 ? null : (discovery as ContactDiscoveryEnvelopeLegacy | null | undefined);

  const searched = Number((v2 ? v2.counts?.searchedRaw : legacy?.searched) ?? 0);
  const accepted = Number((v2 ? v2.counts?.accepted : legacy?.preValidation?.accepted ?? searched) ?? 0);
  const dropped = Number((v2 ? v2.counts?.dropped : legacy?.preValidation?.dropped) ?? 0);
  const created = Number((v2 ? v2.counts?.persisted?.created : legacy?.created) ?? 0);
  const updated = Number((v2 ? v2.counts?.persisted?.updated : legacy?.updated) ?? 0);
  const stopReason = v2 ? String(v2.cost?.stopReason ?? 'COMPLETED') : 'COMPLETED';
  const settledCredits = v2 ? Number(v2.cost?.settledCredits ?? 0) : null;

  const statusText = v2
    ? `searched ${searched} | accepted ${accepted} | dropped ${dropped} | created ${created} | updated ${updated} | stop ${stopReason}${Number.isFinite(settledCredits as number) ? ` | credits ${settledCredits}` : ''}`
    : `searched ${searched} | accepted ${accepted} | dropped ${dropped} | created ${created} | updated ${updated}`;

  return { searched, accepted, dropped, created, updated, stopReason, settledCredits, statusText };
};

/**
 * Does this subject belong to the Source wing? From there the header's up-arrow reads
 * "Back to dashboard" instead of "Back to list".
 */
const isSourceWingSubject = (subject: ActiveEntityFocus | null): boolean => {
  if (!subject) return false;
  if (isEntityFocusSubject(subject)) {
    return SOURCE_DOMAIN_ENTITY_TYPES.has(subject.entityType);
  }
  // A Source-wing TABLE-focus subject (the Assets / Tenants / Contacts / Leads / Fits tables,
  // reached from a Database folder, a resumed session, or the legacy "Back to list") owns no
  // entity, so both isEntityFocusSubject and listFocusTarget are false and the header rendered
  // NO up-arrow at all, a dead end ("I land in a bulk leads table and can't get back to the
  // dashboard"). Treat it as source-wing so the up-arrow returns to the dashboard here too.
  if (isTableFocusSubject(subject)) {
    const view = resolvePortalViewFromTableFocus(subject);
    const tableEntityType = view ? SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE[view] : null;
    return Boolean(tableEntityType && SOURCE_DOMAIN_ENTITY_TYPES.has(tableEntityType));
  }
  return false;
};

export const useLeasingWorkflows = ({ kernel }: useLeasingWorkflowsArgs): useLeasingWorkflowsReturnType => {
  const rentRollOpenGeneration = useRef(0);
  const studioOpenGeneration = useRef(0);
  const assetLoadGeneration = useRef(0);
  const assetLoadOwner = useRef<string | null>(null);
  const loadSourceAssets = useCallback(async () => {
    const k = kernel.current;
    if (!k) return;
    const generation = assetLoadGeneration.current + 1;
    const owner = () => {
      const user = k.getUserContext();
      return `${user.dbUserId}:${user.organisationId}:${user.authenticatedOrganisationId ?? ''}:${user.isImpersonating}`;
    };
    const initialOwner = owner();
    if (k.getState().sourceAssets.status === 'loading' && assetLoadOwner.current === initialOwner) return;
    assetLoadOwner.current = initialOwner;
    assetLoadGeneration.current = generation;
    const stillCurrent = () => {
      if (assetLoadGeneration.current !== generation) return false;
      if (owner() === initialOwner) return true;
      k.setState({ sourceAssets: { ids: [], status: 'idle' } });
      return false;
    };
    k.setState({ sourceAssets: { ids: [], status: 'loading' } });
    try {
      const ids: string[] = [];
      let offset = 0;
      while (true) {
        const page = await k.queryInventory('Property', { limit: 200, offset, force: true });
        if (!stillCurrent()) return;
        if (page.error) throw new Error(page.error);
        ids.push(...page.ids);
        if (!page.pagination?.hasMore) break;
        if (!page.ids.length) throw new Error('Asset loading stopped before the final page.');
        offset += page.ids.length;
      }
      const portfolioIds = [...new Set(ids.map(id => k.getInventory().Property.registry[id]?.portfolioId).filter((id): id is string => Boolean(id)))];
      await k.ensureInventoryIds('Portfolio', portfolioIds);
      if (!stillCurrent()) return;
      k.setState({ sourceAssets: { ids: [...new Set(ids)], status: 'ready' } });
    } catch (error) {
      if (!stillCurrent()) return;
      k.setState({ sourceAssets: { ids: [], status: 'error', error: error instanceof Error ? error.message : 'Could not load assets.' } });
      k.handleError(error instanceof Error ? error.message : 'Could not load Source assets.');
    }
  }, [kernel]);

  const loadSourceRentRoll = useCallback(async (propertyId: string) => {
    const k = kernel.current;
    if (!k || k.readEntityRuntimeState('Property', propertyId).rentRoll?.status === 'loading') return;
    const patch = (value: Partial<NonNullable<ReturnType<typeof k.readEntityRuntimeState<'Property'>>['rentRoll']>>) => {
      const current = k.readEntityRuntimeState('Property', propertyId).rentRoll;
      k.patchEntityRuntimeState('Property', propertyId, { rentRoll: { status: 'loading', unitIds: [], dealIds: [], ...current, ...value } });
    };
    patch({ status: 'loading', error: undefined });
    try {
      if (!await k.syncEntity('Property', propertyId, { force: true, requireFresh: true })) throw new Error('Asset not found or access denied.');
      const load = async (entity: 'Unit' | 'Deal') => {
        const ids: string[] = [];
        let offset = 0;
        while (true) {
          const page = await k.queryInventory(entity, { filters: { propertyId }, limit: 200, offset, force: true });
          if (page.error) throw new Error(page.error);
          ids.push(...page.ids);
          if (!page.pagination?.hasMore) break;
          if (!page.ids.length) throw new Error('Rent-roll loading stopped before the final page.');
          offset += page.ids.length;
        }
        return [...new Set(ids)];
      };
      const [unitIds, dealIds] = await Promise.all([load('Unit'), load('Deal')]);
      const inventory = k.getInventory();
      const brandIds = [...new Set(dealIds.map(id => inventory.Deal.registry[id]?.brandId).filter((id): id is string => Boolean(id)))];
      const floorIds = [...new Set(unitIds.flatMap(id => inventory.Unit.registry[id]?.floorIds ?? []))];
      // Historical leases still refer to closed retailers, which the discovery list hides.
      for (let offset = 0; offset < brandIds.length; offset += 100) {
        const page = await k.queryInventory('Brand', { filters: { ids: brandIds.slice(offset, offset + 100), includeLiquidated: true }, limit: 100, force: true });
        if (page.error) throw new Error(page.error);
      }
      const hydrated = await k.ensureInventoryIds('Floor', floorIds);
      if (hydrated.errors.length || hydrated.unresolvedIds.length || brandIds.some(id => !k.getInventory().Brand.registry[id]?.name)) throw new Error('Some tenant or floor records could not be loaded. Retry to view the full rent roll.');
      patch({ status: 'ready', unitIds, dealIds });
    } catch (error) {
      patch({ status: 'error', error: error instanceof Error ? error.message : 'Could not load the rent roll.' });
      k.handleError(error instanceof Error ? error.message : 'Could not load the rent roll.');
    }
  }, [kernel]);

  const saveSourceRentRollRecord = useCallback(async (propertyId: string, entity: 'Unit' | 'Deal', id: string, fieldKey: string) => {
    const k = kernel.current;
    if (!k) return;
    const edit = k.readEntityRuntimeState(entity, id).sourceRentRollEdit;
    if (!edit || edit.saving || edit.refreshRequired || !Object.hasOwn(edit.values, fieldKey)) return;
    const fieldValue = edit.values[fieldKey];
    if (typeof fieldValue !== 'string') return;
    const patchEdit = (next: typeof edit | undefined) => {
      k.patchEntityRuntimeState(entity, id, { sourceRentRollEdit: next });
    };
    patchEdit({ ...edit, saving: true, error: undefined });
    try {
      const inv = k.getInventory();
      const property = inv.Property.registry[propertyId];
      if (!canEditSourceRentRoll(k.getUserContext(), property?.leasingManagerIds)) throw new Error('Only Fater admins and this asset’s assigned owners can edit its rent roll.');
      const unit = entity === 'Unit' ? inv.Unit.registry[id] : inv.Unit.registry[inv.Deal.registry[id]?.unitId ?? ''];
      if (!unit || unit.propertyId !== propertyId) throw new Error('The record does not belong to this asset.');
      const results = await k.mutateInventory([{ entity, action: 'UPDATE', id, payload: { ...rentRollPayload(entity, { [fieldKey]: fieldValue }), expectedUpdatedAt: edit.expectedUpdatedAt } }], 'Update Source master rent roll');
      const values = { ...edit.values };
      delete values[fieldKey];
      // Only our write's version may advance other pending fields, never a later external refresh.
      const expectedUpdatedAt = results[0]?.updatedAt ?? edit.expectedUpdatedAt;
      const remaining = Object.keys(values).length ? { values, expectedUpdatedAt } : undefined;
      try {
        if (!await k.syncEntity(entity, id, { force: true, requireFresh: true })) throw new Error('Record refresh unavailable.');
        patchEdit(remaining);
      } catch {
        patchEdit({ values, expectedUpdatedAt, refreshRequired: true, error: 'Saved, but the latest record could not be loaded. Reload before editing again.' });
      }
      k.logUserAction('Source rent roll saved', { propertyId, entity, id, fields: [fieldKey] });
    } catch (error) {
      patchEdit({ ...edit, saving: false, error: error instanceof Error ? error.message : 'Save failed. Your draft is retained.' });
      k.handleError(error instanceof Error ? error.message : 'Could not save the rent roll.');
    }
  }, [kernel]);
    const focusTableUI = useCallback(async (view: SessionTableFocusView, options?: { isStillWanted?: () => boolean }) => {
    const k = kernel.current;
    if (!k) return;
    // The Organisations list is a section of Settings' Admin group, not a session table.
    if (view === 'organisations') {
      const { isFaterAdmin, isOrganisationAdmin } = k.getUserContext();
      if (isFaterAdmin || isOrganisationAdmin) k.openModal('UserSettings', { section: 'workspace-general' });
      return;
    }
    const tableFocus = resolveTableFocusFromPortalView(view);
    if (!tableFocus) return;

    // Browsing a table is a subject move on the conversation you are already in: subjects stack
    // and the last one drives the view. This function used to reuse the active session only when
    // it "could hold" the table focus, then hunt for the table's own conversation, then mint one -
    // all of it working around setActiveSubject refusing to move a session off its anchor. The
    // refusal is gone, and the session churn it forced goes with it.
    let sessionId = k.getActiveChatSessionId();
    const activeSession = sessionId ? k.getInventory().ChatSession.registry[sessionId] ?? null : null;
    if (!sessionId || !activeSession) {
      sessionId = await k.ensureSession({
        nameHint: `${tableFocus.entityType} Table Session`,
        initialSubject: tableFocus,
        isStillWanted: options?.isStillWanted,
      });
    }
    if (!sessionId || options?.isStillWanted?.() === false) return;
    if (k.getActiveChatSessionId() !== sessionId) {
      await k.activateChatSession(sessionId, options);
    }
    if (options?.isStillWanted?.() === false) return;
    k.setActiveSubject(sessionId, tableFocus);
  }, [kernel]);

  // In the Source wing (portfolio/asset/unit/brand/contact subjects) the up-arrow is
  // "Back to dashboard". This is the GATE + fallback: it is non-null whenever the focused
  // subject belongs to the Source wing, so the arrow renders and reads "Back to dashboard".
  // Elsewhere it is null and the arrow keeps its historical "Back to list" behavior (the entity
  // type's table view). The ACTUAL landing portfolio is resolved at click time from the focused
  // entity's OWN portfolio (see resolveSourceDashboardTarget), the org default / empty dashboard
  // here is only the last-resort fallback.
  const resolveSourceDashboardFallbackPortfolioId = useCallback((): string | null => {
    const k = kernel.current;
    if (!k) return null;
    if (!isSourceWingSubject(selectActiveSubjectFromInventoryState(k.getState()))) return null;
    const orgId = k.getUserContext?.()?.organisationId ?? null;
    // Never dead-end the back-to-dashboard: if the org has no configured default portfolio, fall
    // back to the empty-portfolio dashboard (the same fallback the Source nav button uses), so
    // from any source-wing view the up-arrow always returns to the Source dashboard.
    return resolveDefaultSourcePortfolioId(orgId) ?? SOURCE_EMPTY_PORTFOLIO_ID;
  }, [kernel]);

  // Resolve the dashboard the up-arrow should open to the FOCUSED ENTITY'S OWN portfolio, so it
  // always shows the campaigns of the entity you were just looking at, org-independent, not the
  // hardcoded org->portfolio map (which only knows Fater->Altarea and dead-ended every other case
  // on the empty dashboard). Read LIVE from the inventory registry at click time: the focused
  // entity often hydrates after the header first renders, so a value memoized on the subject
  // alone would go stale. Falls back to the org default / empty dashboard when the focused entity
  // carries no portfolio link (e.g. Brand / Contact subjects).
  const resolveSourceDashboardTarget = useCallback((fallbackPortfolioId: string | null): string | null => {
    const k = kernel.current;
    // Resolve the org-default dashboard FRESH at click time. `fallbackPortfolioId` is the value
    // the caller computed at render time, and it can hold the empty-portfolio fallback if
    // getUserContext() had not hydrated the org yet when it first ran on this subject (observed on
    // table-focus subjects: the up-arrow then landed on the empty "No portfolio selected"
    // dashboard instead of the org's populated one).
    const orgId = k?.getUserContext?.()?.organisationId ?? null;
    const orgFallback = resolveDefaultSourcePortfolioId(orgId) ?? fallbackPortfolioId ?? SOURCE_EMPTY_PORTFOLIO_ID;
    const subject = k ? selectActiveSubjectFromInventoryState(k.getState()) : null;
    if (!isEntityFocusSubject(subject)) return orgFallback;
    return resolveSubjectOwningPortfolioId(subject, k?.getInventory?.()) ?? orgFallback;
  }, [kernel]);

  const navigateBackFromSubject = useCallback((fallbackPortfolioId: string | null): SourceBackNavigationOutcome => {
    const k = kernel.current;
    if (!k) return 'none';
    const activeSessionId = k.getActiveChatSessionId();
    const subject = selectActiveSubjectFromInventoryState(k.getState());
    if (!activeSessionId || !subject) return 'none';
    if (fallbackPortfolioId) {
      // Land on the focused entity's OWN portfolio dashboard; fall back to the org default / empty.
      const targetPortfolioId = resolveSourceDashboardTarget(fallbackPortfolioId) ?? fallbackPortfolioId;
      // Leave any campaign/draft sub-state behind so the dashboard renders, not a stale editor.
      k.setState({
        ...(isEntityFocusSubject(subject) && subject.focusActionType === 'RENT_ROLL' ? { sourceDashboardTab: 'assets' as const } : {}),
        sourceOpeningRentRollId: null,
        sourceCreatingCampaign: false,
        sourceSelectedCampaignId: null,
        sourceFocusedFitId: null,
        sourceSelectedFitIds: [],
      });
      // If we could only resolve the EMPTY dashboard (no owning portfolio, no org default,
      // e.g. a table-focus subject in a session whose org isn't mapped), hand back to the caller's
      // canonical "go home to Source" landing, which additionally falls back to the top
      // active-client portfolio (the same place the Source nav button lands). This is what unlocks
      // the bulk leads table: the up-arrow always reaches a POPULATED dashboard, never a dead end.
      if (!targetPortfolioId || targetPortfolioId === SOURCE_EMPTY_PORTFOLIO_ID) return 'source-home';
      void k.focusEntityUI('Portfolio', targetPortfolioId, 'ORCHESTRATE_SOURCING');
      k.logUserAction?.('Source Dashboard Requested', {
        activeSessionId,
        fromEntityType: subject.entityType,
        fromEntityId: subject.entityId,
        toPortfolioId: targetPortfolioId,
      });
      return 'source-dashboard';
    }
    const listFocusTarget = isEntityFocusSubject(subject)
      ? getSessionTableFocusViewForEntityType(subject.entityType)
      : null;
    if (!listFocusTarget) return 'none';
    void focusTableUI(listFocusTarget);
    k.logUserAction?.('List Focus Requested', {
      activeSessionId,
      fromEntityType: subject.entityType,
      fromEntityId: subject.entityId,
      toTableView: listFocusTarget,
    });
    return 'table';
  }, [kernel, focusTableUI, resolveSourceDashboardTarget]);

  const syncDealKernelStateFocus = useCallback((
    dealId: string,
    focusActionType?: LeasingActionType
  ) => {
    const k = kernel.current;
    if (!k) return;
    if (!dealId) return;
    if (!focusActionType) return;

    const nextWorkspaceTab = resolveDealWorkspaceTabForAction(focusActionType);
    const nextEconomicsTab = resolveDealEconomicsTabForAction(focusActionType);
    k.patchEntityRuntimeState(
      'Deal',
      dealId,
      {
        activeTab: nextWorkspaceTab,
        ...(nextEconomicsTab ? { activeEconomicsTab: sanitizeDealEconomicsTab(nextEconomicsTab) } : {}),
        lastFocusedActionType: focusActionType,
      }
    );
  }, [kernel]);

  const promoteToDeal = useCallback(async (params: { unitId: string; missionId: string; lead: LeasingLeadDTO }) => {
    try {
      const response = await fetch('/api/inventory/deals/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      const data = await response.json();
      if (response.status === 409) return { success: true, dealId: data.dealId };
      if (!response.ok) throw new Error(data.message || 'Promotion failed');
      toast.success('Deal draft created');
      return { success: true, dealId: data.dealId };
    } catch (e) {
      toast.error('Failed to create deal draft');
      throw e;
    }
  }, [kernel]);

  const discoverBrandContacts = useCallback(async (
    brandId: string,
    payload: {
      maxResults?: number;
      page?: number;
      enrich?: boolean;
      revealPersonalEmails?: boolean;
      revealPhoneNumber?: boolean;
      domain?: string;
      country?: string;
      countries?: string[];
      titles?: string[];
      seniorities?: string[];
      debug?: boolean;
      allocatedBudgetCredits?: number;
    }
  ): Promise<ContactDiscoveryResult> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');

    console.info('[ContactsDiscovery] Launch', { brandId, payload });
    const complete = k.beginSourceAction?.({ action: 'contacts', origin: 'unknown', data: { entityType: 'Brand', entityId: brandId } });
    try {
    const response = await fetch(`/api/inventory/brands/${brandId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      console.error('[ContactsDiscovery] API failure', {
        brandId,
        status: response.status,
        result,
      });
      throw new Error((result as any)?.message || 'Contact discovery failed');
    }

    const envelope = (result as { discovery?: ContactDiscoveryEnvelope | null })?.discovery ?? null;
    const contacts = (result as { contacts?: unknown })?.contacts;
    const accepted = envelope?.schemaVersion === 'CONTACT_DISCOVERY_V2' ? envelope.counts?.accepted : envelope?.preValidation?.accepted;
    if (Array.isArray(contacts) && contacts.every((contact: unknown) => contact && typeof contact === 'object' && typeof (contact as { id?: unknown }).id === 'string')) {
      complete?.('client_returned', {
        count: contacts.length,
        ...(typeof accepted === 'number' && Number.isSafeInteger(accepted) && accepted >= 0 && accepted <= 1_000_000 ? { acceptedCount: accepted } : {}),
      });
    }

    await k.syncEntity('Brand', brandId);
    // The brand's contacts query was cached (empty) from before discovery, force a refresh
    // so the freshly-created contacts land in the registry and the table re-renders.
    await k.refreshInventory('Contact', { parentId: brandId, parentType: 'brand' }, undefined, { force: true });

    const contactsCount = Array.isArray((result as any)?.contacts) ? (result as any).contacts.length : null;
    console.info('[ContactsDiscovery] Completed', {
      brandId,
      discovery: envelope,
      contactsCount,
    });

    const summary = summarizeContactDiscovery(envelope);

    if ((process.env.NEXT_PUBLIC_CONTACT_DISCOVERY_DEBUG || '0') === '1') {
      console.groupCollapsed(`[ContactsDiscovery][${brandId}] Summary`);
      console.log('Status:', summary.statusText);
      console.log('Providers scanned:', envelope?.providersScanned ?? null);
      console.log('Providers skipped:', envelope?.providersSkipped ?? null);
      console.log('Attempts:', envelope?.attempts ?? null);
      console.log('Cost:', envelope?.cost ?? null);
      console.log('Full response:', result);
      console.groupEnd();
    }

    return { ...(result as object), summary } as ContactDiscoveryResult;
    } catch (error) {
      complete?.(sourceActionFailure(error));
      throw error;
    }
  }, [kernel]);

  // One paid lookup per contact at a time: the same person's Find phone sits on two cards (Overview
  // and Contacts), and a second click while the first runs (30 s to 3 min) would buy it twice.
  const enrichContactInFlight = useRef(new Map<string, Promise<unknown>>());
  const enrichContact = useCallback(async (contactId: string, brandId?: string) => {
    const running = enrichContactInFlight.current.get(contactId);
    if (running) return running;
    const run = (async () => {
      const k = kernel.current;
      if (!k) throw new Error('Kernel not available');
      const response = await fetch(`/api/inventory/contacts/${contactId}/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((result as any)?.message || 'Contact enrichment failed');
      }
      // A found phone is worth a sound: the lookup ran for minutes and the user has usually looked away.
      if ((result as { cached?: boolean; contact?: { directPhone?: string | null } }).contact?.directPhone && !(result as { cached?: boolean }).cached) {
        k.playUISound('SUCCESS');
      }
      // Refresh the brand's contacts so the found email and phone show.
      if (brandId) {
        await k.refreshInventory('Contact', { parentId: brandId, parentType: 'brand' }, undefined, { force: true });
      }
      return result;
    })();
    enrichContactInFlight.current.set(contactId, run);
    try { return await run; } finally { enrichContactInFlight.current.delete(contactId); }
  }, [kernel]);

  const enrichBrand = useCallback(async (brandId: string, dimension?: string, sourceLogoUrl?: string, cik?: string, siren?: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) return null;

    // Drive the Egyptian-blue canvas progress bar while any enrichment is in flight.
    k.setState((state: any) => ({ sourceEnrichInFlight: (state.sourceEnrichInFlight ?? 0) + 1 }));
    const pulsingDims = dimension ? [dimension] : [...ENRICH_DIMS];
    markEnriching(k, normalizedBrandId, pulsingDims, true);
    try {
      const queryParams = new URLSearchParams();
      if (dimension) queryParams.set('dimension', dimension);
      if (typeof cik === 'string' && cik.trim()) queryParams.set('cik', cik.trim());
      if (typeof siren === 'string' && siren.trim()) queryParams.set('siren', siren.trim());
      const query = queryParams.size > 0 ? `?${queryParams.toString()}` : '';
      const response = await fetch(`/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/enrich${query}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // An image the caller already found. The route downloads and archives it, so this is a
        // shortcut past the provider search, not past the verification.
        body: JSON.stringify(sourceLogoUrl ? { logoUrl: sourceLogoUrl } : {}),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((result as { message?: string })?.message || 'Brand enrichment failed');
      }
      // The endpoint emits a realtime Brand UPDATE; sync as a fallback if it's missed.
      await k.syncEntity('Brand', normalizedBrandId);
      // Contact discovery/enrichment ran server-side too, force-refresh the brand's contacts
      // so enriched fields surface without depending on a live socket (they otherwise only
      // appeared after a manual reload).
      await k.refreshInventory('Contact', { parentId: normalizedBrandId, parentType: 'brand' }, undefined, { force: true }).catch(() => {});
      return result;
    } finally {
      markEnriching(k, normalizedBrandId, pulsingDims, false);
      k.setState((state: any) => ({ sourceEnrichInFlight: Math.max(0, (state.sourceEnrichInFlight ?? 0) - 1) }));
    }
  }, [kernel]);

  /**
   * Enrich a list of brands in one run, with a cooperative pause.
   *
   * `onProgress` is the caller's progress bar (done/total); everything else — pool, pause,
   * chat documentation, toasts — is owned here so the panel is only a render shell.
   */
  const bulkEnrichBrands = useCallback(async (
    brandIds: string[],
    options: { lang: string; onProgress?: (done: number, total: number) => void },
  ): Promise<void> => {
    if (!kernel.current || brandIds.length === 0) return;
    const { lang, onProgress } = options;
    const total = brandIds.length;
    // Fresh run: clear any stop request + stale paused queue.
    kernel.current.setState({ sourceEnrichStopRequested: false, sourceEnrichPausedBrandIds: null });

    const queue = brandIds.slice();
    let done = 0;
    const worker = async () => {
      while (queue.length) {
        // The composer's Stop button raises the flag; drain between brands (in-flight
        // requests finish, the rest of the queue is preserved for resume).
        if (kernel.current?.getState().sourceEnrichStopRequested) break;
        const brandId = queue.shift();
        if (!brandId) break;
        try {
          await kernel.current?.enrichBrand(brandId);
        } catch (error) {
          console.error('[useLeasingWorkflows] enrich failed', brandId, error);
        }
        done += 1;
        onProgress?.(done, total);
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(BULK_ENRICH_CONCURRENCY, total) }, worker));
      const stopped = kernel.current?.getState().sourceEnrichStopRequested && queue.length > 0;
      if (stopped) {
        // Paused: stash the untouched remainder so the user (or the agent) can resume.
        kernel.current?.setState({ sourceEnrichPausedBrandIds: queue.slice(), sourceEnrichStopRequested: false });
        toast.info(
          lang === 'fr'
            ? `Enrichissement en pause, ${done} fait(s), ${queue.length} restant(s).`
            : `Enrichment paused, ${done} done, ${queue.length} remaining.`,
        );
        return;
      }
      toast.success(lang === 'fr' ? `${done} marques enrichies.` : `${done} brands enriched.`);
    } catch (error: any) {
      console.error('[useLeasingWorkflows] bulk enrich failed', error);
      toast.error(error?.message || (lang === 'fr' ? "Échec de l'enrichissement." : 'Enrichment failed.'));
    }
  }, [kernel]);

  /**
   * Run the brand data check and remember its verdict on the brand's runtime overlay.
   *
   * The overlay is the cache: it is per-user, it survives clicking away and back, and it is
   * wiped with the session, which is exactly the lifetime of a reading of the data. Without it
   * the panel would have to re-run a billed model call every time the row regains focus.
   */
  const checkBrandData = useCallback(async (brandId: string, lang: string): Promise<BrandDataCheck | null> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) return null;

    const response = await fetch(`/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/data-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((result as { message?: string })?.message || 'Brand data check failed');
    }
    const check = (result as { check?: BrandDataCheck })?.check ?? null;
    if (check) {
      k.patchEntityRuntimeState('Brand', normalizedBrandId, { dataCheck: check });
      // ALWAYS pull the brand back, not only when something was proved.
      //
      // The check writes on every outcome: a proved figure, an anchored company, or a modelled
      // fallback when nothing could be proved. It was syncing only on VERIFIED, so a brand whose
      // accounts are confidential kept showing an empty panel even though a labelled estimate had
      // just been written for it - which is precisely the case where the user most needs to see
      // something. It also matters because the SHORTLIST list projection does not carry financial
      // records at all, so the estimate only ever arrives on a detail sync.
      await k.syncEntity('Brand', normalizedBrandId);
    }
    return check;
  }, [kernel]);

  /**
   * Load the brand's France openings/closures signal onto its runtime overlay.
   *
   * Same arbitration as `checkBrandData`: a derived sub-resource nobody wants on the Brand DTO
   * (it would then be queried on every brand read), cached on the per-user overlay so that
   * re-opening the Expansion tab does not re-query it. Already loading or loaded stays as is;
   * a previous failure is retried.
   */
  const loadBrandExpansionSignal = useCallback(async (brandId: string): Promise<BrandExpansionSignal | null> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) return null;

    const cached = k.readEntityRuntimeState('Brand', normalizedBrandId).expansionSignal;
    if (cached?.status === 'loading') return null;
    if (cached?.status === 'loaded') return cached.signal;

    k.patchEntityRuntimeState('Brand', normalizedBrandId, { expansionSignal: { status: 'loading' } });
    try {
      const response = await fetch(`/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/openings`);
      if (!response.ok) throw new Error(String(response.status));
      const signal = (await response.json()) as BrandExpansionSignal;
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { expansionSignal: { status: 'loaded', signal } });
      return signal;
    } catch {
      // The chart shows "signal momentarily unavailable"; nothing else depends on this read.
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { expansionSignal: { status: 'failed' } });
      return null;
    }
  }, [kernel]);

  /**
   * The brand's news for its News tab (lib/brands/brandNews.ts), on the runtime overlay like the
   * expansion signal: read once per session, the server refreshing a reading over a day old.
   */
  const loadBrandNews = useCallback(async (brandId: string): Promise<BrandNews | null> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) return null;
    const cached = k.readEntityRuntimeState('Brand', normalizedBrandId).news;
    if (cached?.status === 'loading') return null;
    if (cached?.status === 'loaded') return cached.news;
    k.patchEntityRuntimeState('Brand', normalizedBrandId, { news: { status: 'loading' } });
    try {
      const response = await fetch(`/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/news`, { method: 'POST' });
      if (!response.ok) throw new Error(String(response.status));
      const { news } = (await response.json()) as { news: BrandNews | null };
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { news: { status: 'loaded', news } });
      return news;
    } catch {
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { news: { status: 'failed' } });
      return null;
    }
  }, [kernel]);

  const loadBrandGoogleSignals = useCallback(async (brandId: string): Promise<BrandGoogleSignals | null> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
    if (!normalizedBrandId) return null;
    const cached = k.readEntityRuntimeState('Brand', normalizedBrandId).googleSignals;
    if (cached?.status === 'loading') return null;
    if (cached?.status === 'loaded') return cached.signals;
    k.patchEntityRuntimeState('Brand', normalizedBrandId, { googleSignals: { status: 'loading' } });
    try {
      const response = await fetch(`/api/inventory/brands/${encodeURIComponent(normalizedBrandId)}/google-signals`, { method: 'POST' });
      if (!response.ok) throw new Error(String(response.status));
      const { signals } = (await response.json()) as { signals: BrandGoogleSignals | null };
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { googleSignals: { status: 'loaded', signals } });
      return signals;
    } catch {
      kernel.current?.patchEntityRuntimeState('Brand', normalizedBrandId, { googleSignals: { status: 'failed' } });
      return null;
    }
  }, [kernel]);

  const clearBrandContacts = useCallback(async (brandId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');

    const response = await fetch(`/api/inventory/brands/${brandId}/contacts`, {
      method: 'DELETE',
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((result as any)?.message || 'Failed to clear contacts');
    }

    await k.syncEntity('Brand', brandId);
    return result;
  }, [kernel]);

  const removeBrandContact = useCallback(async (brandId: string, contactId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');

    const response = await fetch(`/api/inventory/brands/${brandId}/contacts/${contactId}`, {
      method: 'DELETE',
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((result as any)?.message || 'Failed to remove contact');
    }

    await k.syncEntity('Brand', brandId);
    return result;
  }, [kernel]);

  const generateDocument = useCallback(async (dealId: string, templateId?: string, documentType?: GeneratedDocumentType) => {
    const k = kernel.current;
    if (!k) return;

    const plannedDocumentType: GeneratedDocumentType = documentType === 'LOI' ? 'LOI' : 'INCENTIVE';
    const plannedActionType = plannedDocumentType === 'LOI' ? 'REDACT_LOI' : 'REDACT_INCENTIVE_LETTER';

    syncDealKernelStateFocus(dealId, plannedActionType);
    k.patchEntityRuntimeState(
      'Deal',
      dealId,
      {
        activeTab: 'DRAFTING',
        draftingPreviewMode: 'TEMPLATE',
        draftingSelectedDocumentType: plannedDocumentType,
        draftingIsGenerating: true,
        draftingPendingDocumentId: null,
        draftingFocusRequestAt: Date.now(),
        lastFocusedActionType: plannedActionType,
      }
    );

    try {
      const response = await fetch('/api/inventory/documents/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealId, templateId, documentType }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Document generation failed.');
      }

      const result = await response.json();

      if (result.document) {
        await k.syncEntity('Deal', dealId);
      }

      const resolvedDocumentType: GeneratedDocumentType =
        result?.document?.type === 'LOI' ? 'LOI' : plannedDocumentType;
      const resolvedActionType = resolvedDocumentType === 'LOI' ? 'REDACT_LOI' : 'REDACT_INCENTIVE_LETTER';
      const generatedStatus = typeof result?.document?.status === 'string' ? result.document.status.toUpperCase() : '';
      const generatedDocReady = generatedStatus === 'SUCCESS' || generatedStatus === 'COMPLETE';
      syncDealKernelStateFocus(dealId, resolvedActionType);

      k.patchEntityRuntimeState(
        'Deal',
        dealId,
        {
          activeTab: 'DRAFTING',
          draftingPreviewMode: generatedDocReady ? 'DOCUMENT' : 'TEMPLATE',
          draftingSelectedDocumentId: generatedDocReady ? (result?.document?.id ?? null) : null,
          draftingSelectedDocumentType: resolvedDocumentType,
          draftingIsGenerating: false,
          draftingPendingDocumentId:
            !generatedDocReady && typeof result?.document?.id === 'string' ? result.document.id : null,
          draftingFocusRequestAt: Date.now(),
          lastFocusedActionType: resolvedActionType,
        }
      );


      return result;
    } catch (error) {
      k.patchEntityRuntimeState(
        'Deal',
        dealId,
        {
          draftingIsGenerating: false,
          draftingPendingDocumentId: null,
          draftingFocusRequestAt: Date.now(),
        }
      );
      throw error;
    }
  }, [kernel, syncDealKernelStateFocus]);

  const downloadGeneratedDocument = useCallback(async (docId: string) => {
    if (!docId || typeof docId !== 'string') {
      throw new Error('downloadGeneratedDocument: invalid document id.');
    }

    const link = document.createElement('a');
    link.href = `/api/inventory/documents/${docId}/view?download=1`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const resolveAudit = useCallback(async (auditId: string, action: AuditResolutionAction, manualOverride?: Record<string, any>) => {
    const k = kernel.current;
    if (!k) return;
    try {
      const auditRegistry = k.getState().inventory.Audit.registry;
      const resolvedAudit = auditRegistry[auditId] as {
        id?: string;
        entityType?: string;
        entityId?: string;
      } | undefined;

      const response = await fetch('/api/inventory/audits/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auditId, action, manualOverride })
      });
      if (!response.ok) throw new Error((await response.json()).message || 'Resolution failed');

      const deletedAuditIds = new Set<string>([auditId]);
      if (
        action === 'TERMINATE_ENTITY' &&
        typeof resolvedAudit?.entityType === 'string' &&
        typeof resolvedAudit?.entityId === 'string'
      ) {
        Object.values(auditRegistry).forEach((candidate) => {
          if (!candidate) return;
          if (candidate.entityType !== resolvedAudit.entityType) return;
          if (candidate.entityId !== resolvedAudit.entityId) return;
          if (typeof candidate.id !== 'string' || candidate.id.length === 0) return;
          deletedAuditIds.add(candidate.id);
        });
      }

      if (deletedAuditIds.size > 0) {
        k.applyInventoryEvents(
          Array.from(deletedAuditIds).map((deletedAuditId) => ({
            entityType: 'Audit' as const,
            eventType: 'DELETE' as const,
            entityId: deletedAuditId,
            payload: { id: deletedAuditId },
          }))
        );
      }

      await k.refreshInventory('Audit', undefined, undefined, { force: true });
      toast.success(`Audit resolved: ${action}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to resolve audit');
    }
  }, [kernel]);

  const fetchLeasingActions = useCallback(async (orgId?: string) => {
    if (!kernel.current) return;

    kernel.current.setIsLeasingActionsLoading(true);
    try {
      const url = orgId ? `/api/inventory/actions?orgId=${orgId}` : '/api/inventory/actions';

      console.log(`[Kernel] Fetching actions for org: ${orgId || 'default'}`);
      const res = await axios.get(url);

      kernel.current.setLeasingActions(res.data.actions);
    } catch (e) {
      console.error('[Kernel] API Error:', e);
    } finally {
      kernel.current.setIsLeasingActionsLoading(false);
    }
  }, [kernel]);

  const focusEntityUI = useCallback(async (
    entityType: EntityType,
    entityId: string,
    focusActionType?: LeasingActionType
  ) => {
    const k = kernel.current;
    if (!k || !entityType || !entityId) return;
    const normalizedEntityId = entityId.trim();
    if (!normalizedEntityId) return;
    // AN ORGANISATION IS NOT A PAGE (owner, 2026-09-29: 'there is no content inside content'). Its
    // settings are Settings' Workspace sections, pointed at it; anything else leaves Settings.
    if (entityType === 'Organisation') {
      k.openModal('UserSettings', { section: 'workspace-general', organisationId: normalizedEntityId });
      return;
    }
    if (k.getState().activeModal === 'UserSettings') k.closeModal();
    // Owned by call, not by project id: a second click on the same project keeps the flag when the first settles.
    let studioOpening = 0;
    try {
      if (entityType === 'Project') {
        const shown = selectActiveSubjectFromInventoryState(k.getState());
        if (shown?.entityType !== 'Project' || shown.entityId !== normalizedEntityId) {
          studioOpening = ++studioOpenGeneration.current;
          k.setState({ studioOpeningProjectId: normalizedEntityId });
        }
        restoreProjectToolView(k, normalizedEntityId);
        const project = k.getInventory().Project.registry[normalizedEntityId];
        const view = k.readProjectRuntimeState(normalizedEntityId).projectViewMode;
        k.setStudioSection(resolveProjectStudioSection(project?.projectType, view, k.getUserContext().permissions));
      }

      // A LEAD IS NEVER A PAGE OF ITS OWN. A lead is one brand judged against one property, and that
      // judgement is already readable where it belongs: expanded under its row in the shortlist, with
      // the same summary, scoring, expansion, stores, revenue and contacts. Opening it as a separate
      // full-width page said the same thing twice, took over the dashboard, and pushed the shortlist
      // the user was reading out of view - which is how the agent answering /interesting ended up
      // replacing the list it had just ranked. Removed on the owner's instruction, 2026-08-16:
      // "this focused view shouldn't exist, we already have detail views in the shortlist."
      //
      // So a focus on a lead expands its row instead. Nothing else changes: the page component is
      // still what the expanded row renders, it just no longer has a second life as a destination.
      if (entityType === 'Lead') {
        k.setState({ sourceFocusedFitId: normalizedEntityId });
        return;
      }

      // Opening an entity SHOWS it. A standalone page (Organisations, Finance, Brands...) owns the
      // screen over any subject, so clicking one of its rows focused the entity behind the page and
      // nothing moved (owner, 2026-09-29: 'when we click on an org, nothing happens').
      if (k.getState().portalStandaloneView) k.getState().setPortalStandaloneView(null);

      // "Agents live in projects": opening a project ALWAYS lands on that project's own
      // studio conversation, its scoped home, switching away from whatever session you
      // were in. The SAME policy applies to a sourcing focus (portfolio/asset/unit): it must
      // land on that entity's SOURCE conversation, never keep an unrelated active session
      // (master, a Studio image-gen chat) rendering next to the campaign. Sentinel ids
      // ("__source_empty__") never get a scoped home.
      // Resolved FIRST because it decides whether we are allowed to mint a session below.
      const scopedHomeModule: 'studio' | 'source' | null =
        ENTITY_FOCUS_SESSION_RELOAD_POLICY[entityType] === true && entityType === 'Project'
          ? 'studio'
          : (focusActionType === 'ORCHESTRATE_SOURCING' || focusActionType === 'RENT_ROLL') &&
              (entityType === 'Portfolio' || entityType === 'Property' || entityType === 'Unit') &&
              !normalizedEntityId.startsWith('__')
            ? 'source'
            : null;

      const openingRentRoll = focusActionType === 'RENT_ROLL';
      const openGeneration = ++rentRollOpenGeneration.current;
      if (openingRentRoll) k.setState({ sourceOpeningRentRollId: normalizedEntityId, sourceOpeningCampaignId: null, sourceSelectedCampaignId: null, sourceCreatingCampaign: false, sourcePreparingCampaign: false, sourceFocusedFitId: null, sourceSelectedFitIds: [] });
      else k.setState({ sourceOpeningRentRollId: null });
      const isStillWanted = () => !openingRentRoll || (rentRollOpenGeneration.current === openGeneration && k.getState().sourceOpeningRentRollId === normalizedEntityId);

      let sessionId = k.getActiveChatSessionId();
      const activeSession = sessionId ? k.getInventory().ChatSession.registry[sessionId] : null;
      if (!sessionId || !activeSession) {
        // NEVER pre-create when this entity has a scoped home. `ensureSession` stamps the
        // new session with THIS entity's creation scope, so the session minted here would
        // pass the `currentIsThisEntityHome` test below and short-circuit the very lookup
        // that resumes the entity's real conversation. That is how every cold entry into
        // Source (fresh tab, reload, deep link) landed on a brand-new empty chat while the
        // user's actual thread sat on the server, and how one portfolio accumulated
        // dozens of empty "Portfolio Focus Session" rows. With a scoped home we leave the
        // session unresolved and let the home lookup below decide; it already creates the
        // home when the entity genuinely has none.
        sessionId = scopedHomeModule
          ? null
          : await k.ensureSession({
              nameHint: `${entityType} Focus Session`,
              // The session is born on this entity's surface even though the subject
              // is focused separately below, stamp the home explicitly.
              creationScope: deriveCreationScopeFromSubject({
                entityType,
                entityId: normalizedEntityId,
              }),
            });
      }

      const hasConversationMessages = (targetSessionId: string): boolean => {
        const session = k.getInventory().ChatSession.registry[targetSessionId];
        if (!session) return false;

        const messageCount =
          typeof session.messageCount === 'number' && Number.isFinite(session.messageCount)
            ? Math.max(0, Math.floor(session.messageCount))
            : 0;
        if (messageCount > 0) return true;

        const messages = k.getChatSessionMessages(targetSessionId);
        return messages.some((message) => message.role === 'user' || message.role === 'assistant');
      };

      if (scopedHomeModule) {
        const currentSession = sessionId ? k.getInventory().ChatSession.registry[sessionId] : null;
        const currentIsThisEntityHome =
          currentSession?.creationModule === scopedHomeModule &&
          currentSession?.creationEntityType === entityType &&
          currentSession?.creationEntityId === normalizedEntityId &&
          !(isEntityFocusSubject(currentSession.activeSubject) && currentSession.activeSubject.campaignId);

        if (!currentIsThisEntityHome) {
          // This entity's scoped sessions (indexed by creationScope), newest
          // first. Prefer one that already holds a conversation; else reuse the
          // latest existing one (even empty) so an entity keeps a SINGLE home
          // instead of accumulating an empty session on every open.
          const scopedHomeKey = `${scopedHomeModule}|${entityType}|${normalizedEntityId}`;
          const requestScopedSessions = async (): Promise<ScopedHomeSessionRow[]> => {
            try {
              const response = await axios.get('/api/inventory/sessions', {
                params: {
                  creationModule: scopedHomeModule,
                  creationEntityType: entityType,
                  creationEntityId: normalizedEntityId,
                  limit: 50,
                },
                withCredentials: true,
              });
              const rows = Array.isArray(response.data?.chatSessions)
                ? (response.data.chatSessions as Array<{
                    id?: unknown;
                    updatedAt?: unknown;
                    createdAt?: unknown;
                    messageCount?: unknown;
                    ownerId?: unknown;
                    activeSubject?: { campaignId?: unknown } | null;
                  }>)
                : [];
              // SOURCE HOMES ARE PERSONAL. The list is org-wide (and cross-org for a Fater admin), so
              // the "most recent with messages" rule landed a Fater account inside a client's own
              // Source conversation (bbitoune@altarea.com's, Altarea portfolio), where its test
              // chatter was then written for the client to read (preview, 2026-09-26). A shared
              // conversation is still reachable when opened on purpose; it is just never a default.
              const myUserId = k.getState().userContext.dbUserId;
              return rows
                .filter((row) => scopedHomeModule !== 'source' || !myUserId || row?.ownerId === myUserId)
                // A conversation bound to a Source campaign belongs to that campaign and can never
                // be an entity's home conversation: adopting it would resurface a campaign's chat
                // on the dashboard, and the home re-stamp would rewrite its subject.
                .filter(
                  (row) =>
                    !(
                      typeof row?.activeSubject?.campaignId === 'string' &&
                      row.activeSubject.campaignId.trim().length > 0
                    ),
                )
                .map((row) => ({
                  id: typeof row?.id === 'string' ? row.id.trim() : '',
                  updatedAtMs: new Date(
                    (typeof row?.updatedAt === 'string' && row.updatedAt) ||
                      (typeof row?.createdAt === 'string' && row.createdAt) ||
                      0,
                  ).getTime(),
                  messageCount:
                    typeof row?.messageCount === 'number' && Number.isFinite(row.messageCount)
                      ? Math.max(0, Math.floor(row.messageCount))
                      : 0,
                }))
                .filter((row) => row.id.length > 0);
            } catch (error) {
              if (openingRentRoll) throw error;
              console.warn('[LeasingWorkflows] Failed to list project-scoped chat sessions.', error);
              return [];
            }
          };

          const fetchScopedSessions = async (): Promise<ScopedHomeSessionRow[]> => {
            const alreadyAsking = inFlightScopedHomeSessions.get(scopedHomeKey);
            if (alreadyAsking) return alreadyAsking;

            const request = requestScopedSessions();
            inFlightScopedHomeSessions.set(scopedHomeKey, request);
            try {
              return await request;
            } finally {
              inFlightScopedHomeSessions.delete(scopedHomeKey);
            }
          };

          const scopedSessions = await fetchScopedSessions();
          if (!isStillWanted()) return;
          let homeSessionId: string | null = null;
          let latestExistingId: string | null = null;
          let latestExistingAt = -1;
          let bestWithMessagesId: string | null = null;
          let bestWithMessagesAt = -1;

          // The list endpoint already reports a DB-computed COMMUNICATION count per
          // session, so pick the home from the response alone. Previously this loop
          // `syncEntity`d each candidate in sequence just to read messageCount off the
          // registry, up to 50 serial round trips on every project open, and it picked
          // the first candidate in list order rather than the most recently used one.
          for (const candidate of scopedSessions) {
            if (candidate.updatedAtMs >= latestExistingAt) {
              latestExistingAt = candidate.updatedAtMs;
              latestExistingId = candidate.id;
            }
            const hasMessages =
              candidate.messageCount > 0 || hasConversationMessages(candidate.id);
            if (hasMessages && candidate.updatedAtMs >= bestWithMessagesAt) {
              bestWithMessagesAt = candidate.updatedAtMs;
              bestWithMessagesId = candidate.id;
            }
          }

          // A scoped session is not the only place an entity's conversation can live.
          // Work that starts from a global/master chat, "make me a project like this"
          //, produces messages that carry the entity as their timeline target while
          // the session itself was born global (creationEntityId null). Resolving by
          // creation scope alone therefore finds nothing, mints a fresh session, and
          // the user reopens their project onto an empty chat with the real
          // conversation stranded in the chat they started from. So when no scoped
          // session holds a conversation, adopt the session that actually does.
          if (!bestWithMessagesId && !openingRentRoll) {
            try {
              const response = await axios.get('/api/inventory/timeline-events', {
                params: {
                  targetEntityType: entityType,
                  targetEntityId: normalizedEntityId,
                  category: 'COMMUNICATION',
                  sortDir: 'desc',
                  limit: 50,
                },
                withCredentials: true,
              });
              const events = Array.isArray(response.data?.timelineEvents)
                ? (response.data.timelineEvents as Array<{ chatSessionId?: unknown }>)
                : [];
              // Events come back newest-first, so this preserves recency order.
              const candidateIds: string[] = [];
              events.forEach((event) => {
                const id = typeof event?.chatSessionId === 'string' ? event.chatSessionId.trim() : '';
                if (id.length > 0 && !candidateIds.includes(id)) candidateIds.push(id);
              });

              if (candidateIds.length > 0) {
                // Only adopt a session that is homeless or already belongs to this
                // entity. A session that is another entity's scoped home is off
                // limits: agents can act on entity A from inside B's project chat,
                // and adopting there would drag B's whole conversation into A.
                let adoptedId: string | null = null;
                try {
                  const scopeResponse = await axios.get('/api/inventory/sessions', {
                    params: { ids: candidateIds.join(','), limit: candidateIds.length },
                    withCredentials: true,
                  });
                  const scopeRows = Array.isArray(scopeResponse.data?.chatSessions)
                    ? (scopeResponse.data.chatSessions as Array<{
                        id?: unknown;
                        creationEntityId?: unknown;
                        ownerId?: unknown;
                        activeSubject?: { campaignId?: unknown } | null;
                      }>)
                    : [];
                  // Same rule as the scoped list: a Source home is only ever the user's own.
                  const adoptionOwner = k.getState().userContext.dbUserId;
                  const ownerBySessionId = new Map<string, string>();
                  const campaignBoundSessionIds = new Set<string>();
                  scopeRows.forEach((row) => {
                    if (scopedHomeModule === 'source' && adoptionOwner && row?.ownerId !== adoptionOwner) return;
                    const id = typeof row?.id === 'string' ? row.id.trim() : '';
                    const owner = typeof row?.creationEntityId === 'string' ? row.creationEntityId.trim() : '';
                    if (!id) return;
                    ownerBySessionId.set(id, owner);
                    if (
                      typeof row?.activeSubject?.campaignId === 'string' &&
                      row.activeSubject.campaignId.trim().length > 0
                    ) {
                      campaignBoundSessionIds.add(id);
                    }
                  });
                  adoptedId =
                    candidateIds.find((id) => {
                      // Same rule as the scoped list above: a campaign's own conversation is off
                      // limits as anyone's home.
                      if (campaignBoundSessionIds.has(id)) return false;
                      if (!ownerBySessionId.has(id) && scopedHomeModule === 'source') return false;
                      const owner = ownerBySessionId.get(id);
                      return owner === '' || owner === undefined || owner === normalizedEntityId;
                    }) ?? null;
                } catch {
                  // Scope check unavailable: take the most recent rather than nothing, except in
                  // Source, where an unchecked adoption can land in someone else's thread.
                  adoptedId = scopedHomeModule === 'source' ? null : candidateIds[0] ?? null;
                }
                if (adoptedId) bestWithMessagesId = adoptedId;
              }
            } catch (error) {
              // Best-effort: fall through to the scoped/new session below.
              console.warn('[LeasingWorkflows] Failed to resolve a conversation from the entity timeline.', error);
            }
          }

          homeSessionId = bestWithMessagesId ?? latestExistingId;

          // No session belongs to this entity yet, create its home so the
          // entity owns its conversation from the first open.
          if (!homeSessionId) {
            homeSessionId = await k.createAndActivateSession({
              nameHint: scopedHomeModule === 'studio' ? 'Project' : 'Sourcing',
              initialSubject: { entityType, entityId: normalizedEntityId, ...(focusActionType ? { focusActionType } : {}) },
              creationScope: { module: scopedHomeModule, entityType, entityId: normalizedEntityId },
              isStillWanted,
            });
          }

          if (!isStillWanted()) return;
          if (!homeSessionId && openingRentRoll) throw new Error('Could not open this asset’s conversation. Please try again.');
          if (homeSessionId) sessionId = homeSessionId;
        }
      }

      // No session to focus: either `ensureSession` failed (no scoped home) or the
      // scoped-home lookup found none and could not create one. A no-op, not a crash.
      if (!sessionId || !isStillWanted()) return;

      if (k.getActiveChatSessionId() !== sessionId) {
        await k.activateChatSession(sessionId, { isStillWanted });
      }
      if (!isStillWanted() || k.getActiveChatSessionId() !== sessionId) return;
      k.setActiveSubject(sessionId, {
        entityType,
        entityId: normalizedEntityId,
        ...(focusActionType ? { focusActionType } : {}),
      });
      if (openingRentRoll) k.setState({ sourceOpeningRentRollId: null, sourceDashboardTab: 'assets' });
    } finally {
      if (studioOpening && studioOpenGeneration.current === studioOpening) k.setState({ studioOpeningProjectId: null });
    }
  }, [kernel]);

  // URL-driven entity focus (?fx=<EntityType>:<id> [&ns=1] [&tab=...]): drop the active
  // conversation, then focus the entity — minting a dedicated session first when the URL
  // asked for one. Synchronous on purpose: the portal bootstrap and the popstate handler
  // fire it and move on, the focus work runs detached.
  const applyPortalFocusTarget = useCallback((focusTarget: NonNullable<PortalUrlState['focusTarget']>) => {
    const k = kernel.current;
    if (!k) return;
    const { entityType, entityId, forceNewSession, tab } = focusTarget;
    // parsePortalUrlState already resolved the tab against PORTAL_FOCUS_TAB_MAP, so a
    // non-null tab guarantees this entity type carries one AND the value is allowed.
    // This replaces an eleven-case switch that re-declared the same per-entity unions
    // by hand and had already drifted from that map (it still listed Property's 'FITS').
    // The cast is unavoidable, not a shortcut: with entityType unnarrowed TS collapses
    // KernelEntityRuntimeState<K> to one arbitrary member of the union. The allowlist
    // that makes this sound lives in portal-url-state.ts — keep it the only one.
    const applyFocusTab = () => {
      if (!tab) return;
      k.patchEntityRuntimeState(entityType, entityId, { activeTab: tab } as never);
    };
    k.clearActiveChatSession();

    void (async () => {
      try {
        applyFocusTab();
        if (forceNewSession) {
          const createdSessionId = await k.createAndActivateSession({
            nameHint: `${entityType} Focus Session`,
            initialSubject: { entityType, entityId },
          });
          if (createdSessionId) {
            applyFocusTab();
            return;
          }
        }
        await k.focusEntityUI(entityType, entityId);
        applyFocusTab();
      } catch {
        // Swallow URL-driven focus errors to avoid blocking portal bootstrap.
      }
    })();
  }, [kernel]);

  const triggerActionUI = useCallback(async (action: { type: LeasingActionType; entityType?: EntityType; entityId?: string }) => {
    const k = kernel.current;
    if (!k) return;

    const { type, entityId, entityType: rawEntityType } = action;

    if (entityId && rawEntityType) {
      await focusEntityUI(rawEntityType, entityId, type);
      return;
    }

    const sessionId = k.getActiveChatSessionId();
    if (!sessionId) return;
    k.setActiveSubject(sessionId, null);
  }, [kernel, focusEntityUI]);

  // Persist the active sourcing target selection onto an entity's runtime overlay,
  // skipping the write when the stored triple already matches (no redundant
  // autosave). Kept branched per entity type so the runtime-state generic stays sound.
  const setSourcingSelection = useCallback((
    targetType: 'Portfolio' | 'Property' | 'Unit',
    targetId: string,
    selection: SourcingSelectionTriple,
  ) => {
    const k = kernel.current;
    const normalizedTargetId = typeof targetId === 'string' ? targetId.trim() : '';
    if (!k || !normalizedTargetId) return;

    const existing = (() => {
      if (targetType === 'Portfolio') {
        return normalizeSourcingSelection(k.readEntityRuntimeState('Portfolio', normalizedTargetId).sourcingSelection);
      }
      if (targetType === 'Property') {
        return normalizeSourcingSelection(k.readEntityRuntimeState('Property', normalizedTargetId).sourcingSelection);
      }
      return normalizeSourcingSelection(k.readEntityRuntimeState('Unit', normalizedTargetId).sourcingSelection);
    })();

    if (
      existing.portfolioId === selection.portfolioId &&
      existing.propertyId === selection.propertyId &&
      existing.unitId === selection.unitId
    ) {
      return;
    }

    const nextSelection: SourcingTargetRuntimeState = {
      portfolioId: selection.portfolioId || null,
      propertyId: selection.propertyId || null,
      unitId: selection.unitId || null,
      updatedAt: Date.now(),
    };

    if (targetType === 'Portfolio') {
      k.patchEntityRuntimeState('Portfolio', normalizedTargetId, { sourcingSelection: nextSelection });
      return;
    }
    if (targetType === 'Property') {
      k.patchEntityRuntimeState('Property', normalizedTargetId, { sourcingSelection: nextSelection });
      return;
    }
    k.patchEntityRuntimeState('Unit', normalizedTargetId, { sourcingSelection: nextSelection });
  }, [kernel]);

  // Resolve the sourcing target the active session already carries: its own subject
  // first, then the most recent sourcing-bearing entry of its subject history.
  const resolveStoredSourcingTarget = useCallback((): SourcingTargetSelection | null => {
    const k = kernel.current;
    if (!k) return null;

    const sessionId = k.getActiveChatSessionId();
    if (!sessionId) return null;

    const session = k.getInventory().ChatSession.registry[sessionId] ?? null;
    const normalizeId = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

    const resolveFromEntityRuntime = (
      entityType: 'Portfolio' | 'Property' | 'Unit',
      entityId: string
    ): SourcingTargetSelection | null => {
      const normalizedEntityId = normalizeId(entityId);
      if (!normalizedEntityId) return null;

      const selection = (() => {
        if (entityType === 'Portfolio') {
          return k.readEntityRuntimeState('Portfolio', normalizedEntityId).sourcingSelection;
        }
        if (entityType === 'Property') {
          return k.readEntityRuntimeState('Property', normalizedEntityId).sourcingSelection;
        }
        return k.readEntityRuntimeState('Unit', normalizedEntityId).sourcingSelection;
      }) as { portfolioId?: string | null; propertyId?: string | null; unitId?: string | null } | undefined;

      const unitId = normalizeId(selection?.unitId);
      if (unitId) return { entityType: 'Unit', entityId: unitId };
      const propertyId = normalizeId(selection?.propertyId);
      if (propertyId) return { entityType: 'Property', entityId: propertyId };
      const portfolioId = normalizeId(selection?.portfolioId);
      if (portfolioId) return { entityType: 'Portfolio', entityId: portfolioId };

      return null;
    };

    const activeSubject = session?.activeSubject ?? null;
    if (
      activeSubject &&
      isEntityFocusSubject(activeSubject) &&
      (activeSubject.entityType === 'Portfolio' ||
        activeSubject.entityType === 'Property' ||
        activeSubject.entityType === 'Unit')
    ) {
      if (activeSubject.focusActionType === 'ORCHESTRATE_SOURCING') {
        return { entityType: activeSubject.entityType, entityId: activeSubject.entityId };
      }
      const runtimeTarget = resolveFromEntityRuntime(activeSubject.entityType, activeSubject.entityId);
      if (runtimeTarget) return runtimeTarget;
    }

    const history = Array.isArray(session?.subjectHistory) ? session.subjectHistory : [];
    for (let index = history.length - 1; index >= 0; index -= 1) {
      const entry = history[index];
      if (!entry) continue;
      if (entry.entityType !== 'Portfolio' && entry.entityType !== 'Property' && entry.entityType !== 'Unit') continue;
      const normalizedEntityId = normalizeId(entry.entityId);
      if (!normalizedEntityId) continue;

      if (entry.focusActionType === 'ORCHESTRATE_SOURCING') {
        return { entityType: entry.entityType, entityId: normalizedEntityId };
      }

      const runtimeTarget = resolveFromEntityRuntime(entry.entityType, normalizedEntityId);
      if (runtimeTarget) return runtimeTarget;
    }

    return null;
  }, [kernel]);


  return {
    loadSourceAssets,
    loadSourceRentRoll,
    saveSourceRentRollRecord,
    promoteToDeal,
    discoverBrandContacts,
    enrichContact,
    enrichBrand,
    bulkEnrichBrands,
    checkBrandData,
    loadBrandExpansionSignal,
    loadBrandNews,
    loadBrandGoogleSignals,
    clearBrandContacts,
    removeBrandContact,
    syncDealKernelStateFocus,
    generateDocument,
    downloadGeneratedDocument,
    focusEntityUI,
    focusTableUI,
    applyPortalFocusTarget,
    resolveSourceDashboardFallbackPortfolioId,
    navigateBackFromSubject,
    resolveAudit,
    fetchLeasingActions,
    triggerActionUI,
    setSourcingSelection,
    resolveStoredSourcingTarget,
  };
};
